/**
 * Avenue Perfumes — display-currency conversion
 *
 * Shopify can only *present* AED until Shopify Payments verification is
 * finished: `shop.enabledPresentmentCurrencies` is ["AED"], and
 * marketCurrencySettingsUpdate is refused outright while unified markets is
 * on. So a Kuwaiti shopper sees AED prices even though the market lists
 * Kuwait as a country.
 *
 * Until that changes, this rewrites the *displayed* price into the currency of
 * the country the shopper has selected, at the pegged GCC rates below. It is
 * presentation only — the cart, the order and the charge are all still AED,
 * which the switcher panel says in as many words.
 *
 * The choice is remembered in localStorage, not only in Shopify's country
 * cookie. All six countries sit in one GCC market, so the country is in
 * practice a display preference — and Shopify's cookie can be dropped by a
 * cached response or a geolocation prompt, which is what made the currency
 * fall back to AED on the way to a product page. localStorage survives that.
 * Shopify's own country is still re-asserted whenever the shopper saves.
 *
 * When Shopify Payments goes live and local currencies are switched on in
 * Markets, Shopify converts for real and this file should be removed: delete
 * the <script> tag in snippets/localization.liquid.
 *
 * Rates are the standard pegs (all six currencies are pegged to the dollar,
 * so these do not drift):
 *   1 USD = 3.6725 AED = 3.75 SAR = 0.3065 KWD = 0.376 BHD = 0.3845 OMR
 *         = 3.64 QAR
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'ap_country';

  var MAP = {
    AE: { code: 'AED', rate: 1 },
    SA: { code: 'SAR', rate: 1.02110 },
    KW: { code: 'KWD', rate: 0.08346 },
    BH: { code: 'BHD', rate: 0.10239 },
    OM: { code: 'OMR', rate: 0.10470 },
    QA: { code: 'QAR', rate: 0.99115 }
  };

  function remembered() {
    try {
      var value = window.localStorage.getItem(STORAGE_KEY);
      return value && MAP[value] ? value : null;
    } catch (e) {
      // Private mode, blocked storage — fall back to Shopify's country.
      return null;
    }
  }

  function remember(code) {
    try {
      if (MAP[code]) window.localStorage.setItem(STORAGE_KEY, code);
    } catch (e) { /* nothing to do */ }
  }

  var shopifyCountry = (window.AP_COUNTRY || 'AE').toUpperCase();
  var country = remembered() || shopifyCountry;
  var target = MAP[country];

  // The switcher reads these; keeping them on one object means there is a
  // single answer to "which currency is the shopper actually seeing".
  var state = {
    map: MAP,
    key: STORAGE_KEY,
    shopifyCountry: shopifyCountry,
    country: country,
    target: target,
    remember: remember,
    sweeps: 0,
    converted: 0
  };

  window.AP_CURRENCY = state;

  // The pill is rendered server-side from Shopify's country. If the shopper's
  // remembered choice differs, correct it before they notice.
  function paintPill() {
    var token = document.querySelector('[data-ap-currency-token]');
    if (token && target && token.textContent.trim() !== target.code) {
      token.textContent = target.code;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', paintPill);
  } else {
    paintPill();
  }

  // Nothing to convert for the UAE — the store already prices in AED.
  if (!target || target.code === 'AED') return;

  var isArabic = document.documentElement.lang === 'ar';
  var formatter = null;

  // Two decimals for every currency, including the three-decimal ones (KWD,
  // BHD, OMR). "KWD 16.61" reads like a price; "KWD 16.609" reads like the
  // output of a conversion, which is exactly what it should not look like on
  // a product page.
  try {
    formatter = new Intl.NumberFormat(isArabic ? 'ar-AE' : 'en-AE', {
      style: 'currency',
      currency: target.code,
      currencyDisplay: 'code',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  } catch (e) {
    formatter = null;
  }

  function format(value) {
    if (formatter) {
      // Intl uses a non-breaking space between code and amount; a normal space
      // matches the rest of the theme's price styling.
      return formatter.format(value).replace(/ /g, ' ');
    }
    return target.code + ' ' + value.toFixed(2);
  }

  // "AED 199", "AED 1,800", and the Arabic dirham mark if the money format
  // ever changes to it. The digits are required, so a bare currency code — the
  // switcher's own "AED" row, for instance — is never touched.
  var PRICE = /(?:AED|د\.إ)\s*([0-9][0-9.,]*)/g;

  function convert(text) {
    return text.replace(PRICE, function (match, amount) {
      var value = parseFloat(amount.replace(/,/g, ''));
      if (!isFinite(value)) return match;
      return format(value * target.rate);
    });
  }

  var SKIP = {
    SCRIPT: 1, STYLE: 1, TEXTAREA: 1, NOSCRIPT: 1,
    SELECT: 1, OPTION: 1, TEMPLATE: 1
  };

  function walk(node) {
    if (!node) return;

    if (node.nodeType === 3) {
      var text = node.nodeValue;
      if (text && text.indexOf('AED') > -1) {
        var converted = convert(text);
        if (converted !== text) {
          node.nodeValue = converted;
          state.converted++;
        }
      }
      return;
    }

    if (node.nodeType !== 1) return;
    if (SKIP[node.tagName]) return;
    if (node.hasAttribute && node.hasAttribute('data-ap-no-convert')) return;

    var kids = node.childNodes;
    for (var i = 0; i < kids.length; i++) walk(kids[i]);
  }

  function sweep() {
    state.sweeps++;
    walk(document.body);
  }

  // Prices arrive late in a lot of places — the cart drawer and the products it
  // recommends, quick view, variant changes, upsell blocks, Judge.me. Rather
  // than trying to track exactly which nodes changed (an earlier version did,
  // and kept missing the drawer's recommendations), every batch of mutations
  // just triggers one debounced re-sweep of the page. Sweeping is cheap: it
  // only does work where the text contains "AED", and rewriting cannot loop,
  // because converted text no longer matches.
  var scheduled = false;

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.setTimeout(function () {
      scheduled = false;
      sweep();
    }, 60);
  }

  function start() {
    sweep();

    if (typeof MutationObserver !== 'undefined') {
      new MutationObserver(schedule).observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    // Belt and braces for the loads where a section settles after the observer
    // has gone quiet.
    [300, 900, 2000, 4000].forEach(function (delay) {
      window.setTimeout(sweep, delay);
    });
  }

  function boot() {
    if (document.body) start();
    else document.addEventListener('DOMContentLoaded', start);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('load', sweep);
  document.addEventListener('shopify:section:load', schedule);
})();
